import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { BookOpenText, Globe2, Lightbulb, Sparkles } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Slider } from '../ui/slider';
import { Switch } from '../ui/switch';
import { CineCombo } from './CineCombo';
import { AdaptationPicker } from './AdaptationPicker';
import { AccentSelect } from './LanguageAccentCard';
import { CreditPill } from '../AIStudioShared';
import { errMsg, startAutoDirect, writeStory } from '../../lib/aiStudioApi';
import { cn } from '../../lib/utils';

const Seg = ({ value, options, onChange, testId }) => (
  <div className="inline-flex rounded-lg border border-neutral-800 bg-neutral-950 p-0.5">
    {options.map(([v, label, Icon]) => (
      <button key={v} type="button" onClick={() => onChange(v)} data-testid={`${testId}-${v}`}
        className={cn('flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs transition-colors',
          value === v ? 'bg-amber-500 text-neutral-900' : 'text-neutral-400 hover:text-neutral-200')}>
        {Icon && <Icon size={13} />} {label}
      </button>
    ))}
  </div>
);

const clock = (sec) => `${Math.floor(sec / 60)}:${String(sec % 60).padStart(2, '0')}`;

const FILIPINO = /filipino|tagalog|taglish|cebuano|bisaya/i;

/** AI Director (auto mode) and Director's Idea (manual mode): one idea → the whole story as editable scenes, or straight to shots. */
export function AutoDirectorPanel({ project, catalog, onStarted, ideaMode = false }) {
  const [source, setSource] = useState(project.adaptation?.source_id ? 'adapt' : 'original');
  const [storyFirst, setStoryFirst] = useState(true);
  const mtv = project.format === 'mtv';
  const story = ideaMode || storyFirst || mtv;
  const songFit = (catalog.runtime_options || [60, 90, 120, 150, 180]).reduce((a, n) => (Math.abs(n - (project.song_seconds || 0)) < Math.abs(a - (project.song_seconds || 0)) ? n : a), 180);
  const [form, setForm] = useState({
    idea: '', format: project.format || 'film', episodes_count: project.episodes_count || 1,
    runtime_seconds: (project.format === 'mtv' && project.song_seconds ? songFit : project.runtime_seconds) || catalog.default_runtime || 90, pacing: project.pacing || 'smart',
    genre: project.genre || 'Drama', tone: project.tone || 'cinematic',
    language: project.language || 'English', style_preset: project.style_preset || 'prestige-drama',
    accent: project.accent ?? (FILIPINO.test(project.language || '') ? 'filipino' : ''),
  });
  const prev = project.adaptation || {};
  const [adapt, setAdapt] = useState({
    source_id: prev.source_id?.startsWith('custom:') ? '' : prev.source_id || '',
    source_title: prev.source_id?.startsWith('custom:') ? prev.source_title || '' : '',
    culture_id: prev.culture_id || '', twist_level: prev.twist_level || 'balanced', originality_confirmed: false,
  });
  const [sending, setSending] = useState(false);
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const running = project.direction?.status === 'running';
  const eps = form.format === 'series' ? form.episodes_count : 1;
  const pace = (catalog.pacing || []).find((p) => p.id === form.pacing) || { average: 4.5, min: 2, max: 8 };
  const perEp = Math.max(4, Math.min(catalog.limits?.max_scenes_per_episode || 60, Math.round(form.runtime_seconds / pace.average)));
  const shots = eps * perEp;
  const languages = useMemo(() => [...new Set(['English', ...(catalog.scene?.languages || []), ...catalog.cultures.map((c) => c.language)])], [catalog]);
  const presetLabel = catalog.style_presets.find((p) => p.id === form.style_preset)?.label || '';
  const blocker = source === 'original'
    ? (form.idea.trim().length < 10 ? 'Describe your idea in at least one sentence' : '')
    : !(adapt.source_id || adapt.source_title.trim()) ? 'Pick a story to adapt'
      : !adapt.culture_id ? 'Choose the target language & culture'
        : !adapt.originality_confirmed ? 'Tick the originality pledge' : '';

  const existing = (project.scenes || []).length || (project.story?.scenes || []).length;
  const setLanguage = (v) => set({ language: v || 'English', ...(FILIPINO.test(v || '') && !form.accent ? { accent: 'filipino' } : {}) });
  async function submit() {
    if (existing && !window.confirm(story ? 'Write a new story? This replaces the current story, cast and shot list.' : 'Rewrite the screenplay? This replaces the current cast and shot list.')) return;
    setSending(true);
    try {
      const payload = { ...form, format: mtv ? 'film' : form.format, scenes_per_episode: perEp, adaptation: source === 'adapt' ? adapt : null };
      await (story ? writeStory : startAutoDirect)(project.id, payload);
      toast.success(story ? 'The AI is writing your story — edit any scene once it lands' : 'Your AI director is writing the screenplay');
      onStarted();
    } catch (e) { toast.error(errMsg(e)); } finally { setSending(false); }
  }

  return (
    <Card className="border-neutral-800 bg-neutral-900/40 text-neutral-100" data-testid={ideaMode ? 'directors-idea-panel' : 'auto-director-panel'}>
      <CardContent className="space-y-5 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <div className="flex items-center gap-2 text-base font-semibold">
              {mtv ? <><BookOpenText size={16} className="text-amber-300" /> Music video idea</> : ideaMode ? <><BookOpenText size={16} className="text-amber-300" /> Director's Idea</> : <><Sparkles size={16} className="text-amber-300" /> AI Director</>}
            </div>
            <p className="text-xs text-neutral-400">{mtv ? 'What should the video show? The AI writes scenes that follow your song — intro, verses, chorus, bridge — mixing performance and story. No dialogue: the song is the soundtrack.' : ideaMode
              ? 'Type one main idea — the AI writes the whole story as scenes. Edit every scene, then shoot it, or design shots by hand below.'
              : 'One idea in → story, locked cast & locations, shot list and camera plan out.'}</p>
          </div>
          {!mtv && <Seg value={source} onChange={setSource} testId="auto-source"
            options={[['original', 'Original idea', Lightbulb], ['adapt', 'Adapt a famous story', Globe2]]} />}
        </div>

        {source === 'original' ? (
          <Textarea rows={4} value={form.idea} onChange={(e) => set({ idea: e.target.value })} data-testid="auto-idea-input"
            placeholder="e.g. A Manila street-food vendor discovers her late grandmother's recipe book is a map to a hidden family fortune — and three rival relatives want it too."
            className="border-neutral-800 bg-neutral-950 text-sm" />
        ) : (
          <>
            <AdaptationPicker catalog={catalog} value={adapt} onChange={setAdapt} />
            <Textarea rows={2} value={form.idea} onChange={(e) => set({ idea: e.target.value })} data-testid="auto-adapt-direction"
              placeholder="Optional extra direction — e.g. set it during Sinulog festival, make the villain sympathetic…"
              className="border-neutral-800 bg-neutral-950 text-sm" />
          </>
        )}

        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
          {!mtv && (
            <div className="space-y-2">
              <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">Format</div>
              <Seg value={form.format} onChange={(v) => set({ format: v })} testId="auto-format" options={[['film', 'Film'], ['series', 'Series']]} />
            </div>
          )}
          {form.format === 'series' && (
            <div className="space-y-3">
              <div className="flex justify-between text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">
                <span>Episodes</span><span className="text-amber-300" data-testid="auto-episodes-value">{form.episodes_count}</span>
              </div>
              <Slider min={1} max={12} step={1} value={[form.episodes_count]} onValueChange={([v]) => set({ episodes_count: v })} data-testid="auto-episodes-slider" />
            </div>
          )}
          <div className="space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">{mtv ? 'Video' : form.format === 'series' ? 'Episode' : 'Film'} length{mtv && project.song_seconds ? ` · song ${clock(project.song_seconds)}` : ''}</div>
            {mtv ? (
              <div className="rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs text-neutral-300" data-testid="auto-runtime-song">
                {project.song_seconds ? `Follows the song · ${clock(Math.min(300, project.song_seconds))} min` : 'Follows the song (up to 5 min)'}
              </div>
            ) : (
            <select value={form.runtime_seconds} onChange={(e) => set({ runtime_seconds: Number(e.target.value) })} data-testid="auto-runtime-select"
              className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs text-neutral-200">
              {(catalog.runtime_options || [60, 90, 120, 150, 180]).map((n) => <option key={n} value={n}>{`${clock(n)} min`}</option>)}
            </select>
            )}
          </div>
          <div className="space-y-1">
            <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-neutral-500">Shot pacing</div>
            <select value={form.pacing} onChange={(e) => set({ pacing: e.target.value })} data-testid="auto-pacing-select"
              className="w-full rounded-md border border-neutral-800 bg-neutral-950 px-2 py-2 text-xs text-neutral-200">
              {(catalog.pacing || []).map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
            </select>
          </div>
          <CineCombo label="Genre" value={form.genre} options={catalog.genres} onChange={(v) => set({ genre: v })} testId="auto-genre" />
          <CineCombo label="Tone" value={form.tone} options={catalog.tones} onChange={(v) => set({ tone: v })} testId="auto-tone" />
          {source === 'original' && (
            <CineCombo label="Dialogue language" value={form.language} options={languages} allowCustom onChange={setLanguage} testId="auto-language" clearLabel="English" />
          )}
          <AccentSelect catalog={catalog} value={form.accent} onChange={(accent) => set({ accent })} testId="auto-accent" label="Cast accent" />
          <CineCombo label="Visual style" value={presetLabel} options={catalog.style_presets.map((p) => p.label)} testId="auto-style"
            onChange={(label) => set({ style_preset: catalog.style_presets.find((p) => p.label === label)?.id || '' })} />
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 border-t border-neutral-800 pt-4">
          <div className="space-y-2">
            <div className="text-xs text-neutral-400" data-testid="auto-summary">
              {eps} {eps > 1 ? 'episodes' : 'film'} · {clock(form.runtime_seconds)} min{eps > 1 ? ' each' : ''} · ≈{perEp} shots of {pace.min}–{pace.max} s{eps > 1 ? ` (${shots} total)` : ''} · the AI fits every shot to what happens in it
            </div>
            {!ideaMode && (
              <label className="flex items-center gap-2 text-xs text-neutral-300">
                <Switch checked={storyFirst} onCheckedChange={setStoryFirst} data-testid="auto-story-first-switch" />
                Story first — review and edit every scene before any shot is written
              </label>
            )}
          </div>
          <div className="flex items-center gap-2">
            {blocker && <span className="text-[11px] text-amber-300/80" data-testid="auto-blocker-hint">{blocker}</span>}
            <CreditPill cost={story ? eps : 1 + eps * Math.ceil(perEp / (catalog.limits?.shots_per_call || 8))} label={story ? 'to write the story' : 'to write'} data-testid="auto-credit-pill" />
            <Button onClick={submit} disabled={running || sending || !!blocker} className="bg-amber-500 text-neutral-900 hover:bg-amber-400" data-testid="auto-direct-submit">
              {story ? <BookOpenText size={14} className="mr-1" /> : <Sparkles size={14} className="mr-1" />}
              {running ? 'Writing…' : story ? (existing ? 'Write a new story' : 'Write the story') : existing ? 'Rewrite screenplay' : 'Write screenplay & lock cast'}
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
